import React, { useRef } from 'react'
import { Save, FolderOpen } from 'lucide-react'
import { Button } from '@components/ui/Button'
import { Icon } from '@components/ui/Icon'
import { useBrickStore } from '@store/brickStore'
import './ActionButtons.css'

/**
 * ActionButtons Component (Task 4.2.3)
 *
 * Save and Load buttons for scene persistence
 *
 * Features:
 * - Save: Download current scene as JSON file
 * - Load: Upload JSON file and restore scene
 * - Visual feedback for actions
 * - Error handling with callbacks
 *
 * @component
 */

interface ActionButtonsProps {
  onSaveSuccess?: () => void
  onLoadSuccess?: () => void
  onLoadError?: (error: string) => void
  onLoadConfirm?: (callback: () => void) => void // Callback for confirmation dialog
}

export const ActionButtons: React.FC<ActionButtonsProps> = ({
  onSaveSuccess,
  onLoadSuccess,
  onLoadError,
  onLoadConfirm,
}) => {
  const bricks = useBrickStore((state) => state.bricks)
  const saveScene = useBrickStore((state) => state.saveScene)
  const loadScene = useBrickStore((state) => state.loadScene)

  const fileInputRef = useRef<HTMLInputElement>(null)

  // Handle save button click
  const handleSave = () => {
    try {
      saveScene()
      onSaveSuccess?.()
    } catch (error) {
      onLoadError?.(
        error instanceof Error ? error.message : 'Failed to save scene'
      )
    }
  }

  // Handle load button click (open file picker)
  const handleLoadClick = () => {
    fileInputRef.current?.click()
  }

  // Handle file selection
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      // Read file content
      const content = await file.text()

      // If scene has bricks, ask for confirmation before clearing
      if (bricks.size > 0) {
        if (onLoadConfirm) {
          onLoadConfirm(() => loadSceneFromContent(content))
        } else {
          // No confirmation handler, show browser confirm
          if (
            window.confirm(
              `Loading will replace your current scene with ${bricks.size} brick(s). Continue?`
            )
          ) {
            loadSceneFromContent(content)
          }
        }
      } else {
        // Empty scene, load directly
        loadSceneFromContent(content)
      }
    } catch (error) {
      onLoadError?.(
        error instanceof Error ? error.message : 'Failed to read file'
      )
    }

    // Reset file input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Load scene from file content
  const loadSceneFromContent = (content: string) => {
    try {
      loadScene(content)
      onLoadSuccess?.()
    } catch (error) {
      onLoadError?.(
        error instanceof Error ? error.message : 'Failed to load scene'
      )
    }
  }

  const hasBricks = bricks.size > 0

  return (
    <div className="action-buttons">
      {/* Save Button */}
      <Button
        variant="secondary"
        size="medium"
        onClick={handleSave}
        disabled={!hasBricks}
        ariaLabel="Save scene"
      >
        <Icon size="standard">
          <Save size={24} />
        </Icon>
        <span className="action-buttons__label">Save</span>
      </Button>

      {/* Load Button */}
      <Button
        variant="secondary"
        size="medium"
        onClick={handleLoadClick}
        ariaLabel="Load scene"
      >
        <Icon size="standard">
          <FolderOpen size={24} />
        </Icon>
        <span className="action-buttons__label">Load</span>
      </Button>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        aria-label="Select JSON file to load"
      />
    </div>
  )
}
